import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useTags } from '@/features/tags/public';
import { tasksService } from '../api/tasks.service';
import { TASK_KEYS } from './queryKeys';
import { seedTaskEntities } from './useTaskEntityCache';
import { resolveTaskTags } from './useTaskTags';
import type { TaskDetail } from '../types';
import type { Tag } from '@/features/tags/public';

export function useTaskDetail(taskId: string | null) {
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId)!;
  const queryClient = useQueryClient();

  const { data: rawData, ...rest } = useQuery<TaskDetail>({
    queryKey: TASK_KEYS.detail(activeOrganizationId, taskId ?? ''),
    queryFn: async ({ signal }) => {
      const result = await tasksService.getById(taskId!, signal);
      seedTaskEntities(queryClient, activeOrganizationId, [result]);
      return result;
    },
    staleTime: 30_000,
    enabled: !!activeOrganizationId && !!taskId,
    placeholderData: () => {
      const entity = queryClient.getQueryData<TaskDetail>(
        TASK_KEYS.entity(activeOrganizationId, taskId ?? ''),
      );
      // Only use as placeholder if it was previously fetched as a full TaskDetail (has tags).
      // Entities seeded from projectTasks are Task objects and lack tags/attachments/etc.
      return entity && 'tags' in entity ? entity : undefined;
    },
  });

  const { data: projectTagsData } = useTags(rawData?.projectId ?? null);
  const allProjectTags = (projectTagsData?.items ?? []) as Tag[];
  const tags = resolveTaskTags(rawData?.tagIds, allProjectTags);
  const data = rawData ? { ...rawData, tags } : rawData;

  return { ...rest, data };
}
